/**
 * "Saiu da equipe": um representante saiu e outro (novo ou que já existe)
 * assumiu os clientes. Numa ação só (api.trocarRepresentante):
 * - os códigos de quem saiu passam para quem entra A PARTIR do mês escolhido
 *   (os meses anteriores continuam com quem vendia);
 * - as metas de quem saiu, desse mês em diante, passam para quem entra (opcional);
 * - quem saiu fica inativo.
 * Ex. (12/10/2026): Fabricia saiu, Tatielly assumiu: código 42 a partir de 09/2026.
 *
 * Lê: os cadastros (quais códigos são da pessoa no mês: dominio/identificacao.ts).
 * Usado por: telas/cadastros/Representantes.tsx.
 */
import { useState } from 'react';
import { api } from '../../dados/api.ts';
import { mesAtual, mesesDepois } from '../../dominio/datas.ts';
import { codigosParaPassar } from '../../dominio/identificacao.ts';
import type { Cadastros as TipoCadastros, Mes, Representante } from '../../dominio/tipos.ts';
import { listar, NOME_DO_SISTEMA, nomeDoMes } from '../../formatacao.ts';
import { porNome, type Executar } from './comum.ts';

interface Props {
  sai: Representante;
  /** O mês aberto na tela (sugestão para "a partir de"). */
  mes: Mes;
  cadastros: TipoCadastros;
  executar: Executar;
  aoFechar: () => void;
}

const chaveDoCodigo = (c: { fonte: string; codigo: string }) => `${c.fonte}|${c.codigo}`;

export function TrocaDeRepresentante({ sai, mes, cadastros, executar, aoFechar }: Props) {
  const [quem, setQuem] = useState<'novo' | 'existente'>('novo');
  const [novoNome, setNovoNome] = useState('');
  const [entraId, setEntraId] = useState('');
  // A troca vale de um mês que já começou (até 12 meses para trás); uma saída futura se faz quando acontecer.
  const ultimoMes = mesAtual();
  const primeiroMes = mesesDepois(ultimoMes, -12);
  const [desde, setDesde] = useState<Mes>(mes > ultimoMes ? ultimoMes : mes < primeiroMes ? primeiroMes : mes);
  const [moverMetas, setMoverMetas] = useState(true);
  /** Códigos que a gerente desmarcou (o resto passa). */
  const [desmarcados, setDesmarcados] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);

  const codigos = codigosParaPassar(cadastros.codigos, sai.id, desde);
  const passam = codigos.filter((c) => !desmarcados.has(chaveDoCodigo(c)));
  const opcoes = cadastros.representantes.filter((r) => r.id !== sai.id).sort(porNome);
  const nomeDeQuemEntra = quem === 'novo' ? novoNome.trim() : (opcoes.find((r) => r.id === Number(entraId))?.nome ?? '');
  const pronto = quem === 'novo' ? novoNome.trim() !== '' : entraId !== '';
  // "A partir de": do mês atual até 12 meses antes.
  const meses = Array.from({ length: 13 }, (_, i) => mesesDepois(ultimoMes, -i));
  const descreverCodigo = (c: { fonte: 'winthor' | 'sap-bauny'; codigo: string; recebeEm?: Mes }) =>
    `${c.codigo} (${NOME_DO_SISTEMA[c.fonte]}${c.recebeEm ? `, que ${sai.nome} só receberia em ${nomeDoMes(c.recebeEm)}: passa a partir de lá` : ''})`;

  async function confirmar() {
    setEnviando(true);
    const certo = await executar(
      () =>
        api.trocarRepresentante({
          saiId: sai.id,
          ...(quem === 'novo' ? { novoNome: novoNome.trim() } : { entraId: Number(entraId) }),
          desde,
          codigos: passam,
          moverMetas,
        }),
      passam.length > 0
        ? `Troca feita. A partir de ${nomeDoMes(desde)}, ${listar(passam.map(descreverCodigo))} passa${passam.length > 1 ? 'm' : ''} para ${nomeDeQuemEntra}. ${sai.nome} agora aparece como Inativo na lista.`
        : `Troca feita. Nenhum código passou: as vendas continuam com ${sai.nome}. ${sai.nome} agora aparece como Inativo na lista.`,
    );
    setEnviando(false);
    if (certo) aoFechar();
  }

  return (
    <div className="pilha antes-depois troca">
      <h3>{sai.nome} saiu da equipe</h3>
      <div className="linha-campos">
        <div className="segmentado" role="group" aria-label="Quem assume os clientes">
          <button type="button" aria-pressed={quem === 'novo'} onClick={() => setQuem('novo')}>
            Entrou alguém novo
          </button>
          <button type="button" aria-pressed={quem === 'existente'} onClick={() => setQuem('existente')}>
            Alguém da equipe assumiu
          </button>
        </div>
      </div>
      <div className="linha-campos">
        {quem === 'novo' ? (
          <label>
            Nome de quem entrou
            <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} autoFocus />
          </label>
        ) : (
          <label>
            Quem assumiu os clientes
            <select value={entraId} onChange={(e) => setEntraId(e.target.value)}>
              <option value="">Escolha…</option>
              {opcoes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                  {r.ativo ? '' : ' (inativo)'}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          A partir de
          <select
            value={desde}
            onChange={(e) => {
              setDesde(e.target.value);
              setDesmarcados(new Set());
            }}
          >
            {meses.map((m) => (
              <option key={m} value={m}>
                {nomeDoMes(m)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <fieldset className="pilha campos-agrupados">
        <legend>Códigos que passam (de {sai.nome} em {nomeDoMes(desde)})</legend>
        {codigos.length === 0 ? <p className="texto-apagado">Nenhum código de vendedor é de {sai.nome} nesse mês.</p> : null}
        {codigos.map((c) => {
          const chave = chaveDoCodigo(c);
          return (
            <label key={chave} className="campo-checagem">
              <input
                type="checkbox"
                checked={!desmarcados.has(chave)}
                onChange={(e) =>
                  setDesmarcados((atual) => {
                    const novo = new Set(atual);
                    if (e.target.checked) novo.delete(chave);
                    else novo.add(chave);
                    return novo;
                  })
                }
              />
              {descreverCodigo(c)}
            </label>
          );
        })}
        <label className="campo-checagem">
          <input type="checkbox" checked={moverMetas} onChange={(e) => setMoverMetas(e.target.checked)} />
          Passar também as metas de {sai.nome} de {nomeDoMes(desde)} em diante
        </label>
      </fieldset>
      {pronto ? (
        <p className="resumo-da-troca">
          {passam.length > 0 ? (
            <>
              A partir de <strong>{nomeDoMes(desde)}</strong>, as vendas de {listar(passam.map(descreverCodigo))} contam para <strong>{nomeDeQuemEntra}</strong>
              {moverMetas ? ', junto com as metas' : ''}. Até {nomeDoMes(mesesDepois(desde, -1))} continuam com {sai.nome}.
            </>
          ) : (
            <>Nenhum código marcado: as vendas continuam contando para {sai.nome}{moverMetas ? `; só as metas de ${nomeDoMes(desde)} em diante passam para ${nomeDeQuemEntra}` : ''}.</>
          )}{' '}
          {sai.nome} passa a aparecer como Inativo.
        </p>
      ) : null}
      <div className="linha-campos">
        <button type="button" className="botao" onClick={() => void confirmar()} disabled={!pronto || enviando}>
          {enviando ? 'Salvando…' : 'Confirmar a troca'}
        </button>
        <button type="button" className="botao-secundario" onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
